import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { CalendarDays, Check, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/**
 * A Grafana-style time range picker: quick relative ranges on the left, an
 * absolute from/to form on the right.
 *
 * The value is kept *relative* (e.g. "last 30 days") rather than being resolved
 * to timestamps at selection time. Grafana works this way for a reason: a range
 * frozen at the moment you picked it silently goes stale while the page sits
 * open, so a "last 30 days" view opened this morning would quietly stop
 * including today. Resolution to absolute instants happens at query time.
 */

/** Relative ranges, in days. `null` means "everything we have". */
export interface RelativeRange {
  kind: 'relative'
  days: number | null
  label: string
}

export interface AbsoluteRange {
  kind: 'absolute'
  /** YYYY-MM-DD, as produced by <input type="date">. */
  from: string
  to: string
}

export type TimeRange = RelativeRange | AbsoluteRange

export const QUICK_RANGES: RelativeRange[] = [
  { kind: 'relative', days: 1, label: 'Last 24 hours' },
  { kind: 'relative', days: 2, label: 'Last 2 days' },
  { kind: 'relative', days: 7, label: 'Last 7 days' },
  { kind: 'relative', days: 30, label: 'Last 30 days' },
  { kind: 'relative', days: 90, label: 'Last 90 days' },
  { kind: 'relative', days: null, label: 'Everything' },
]

export const DEFAULT_TIME_RANGE: RelativeRange = QUICK_RANGES.find(r => r.days === 30)!

/**
 * Resolve a range to the ISO instants the API expects.
 *
 * Call this at query time, not at selection time, so a relative range stays
 * relative to *now* rather than to whenever the user last touched the control.
 */
export function resolveTimeRange(range: TimeRange): { from?: string; to?: string } {
  if (range.kind === 'absolute') {
    return {
      from: range.from ? new Date(`${range.from}T00:00:00`).toISOString() : undefined,
      // End of the chosen day, so picking a single day includes all of it
      // rather than only rows landing exactly at midnight.
      to: range.to ? new Date(`${range.to}T23:59:59.999`).toISOString() : undefined,
    }
  }
  if (range.days === null) return {}
  const from = new Date(Date.now() - range.days * 24 * 60 * 60 * 1000)
  return { from: from.toISOString() }
}

export function describeTimeRange(range: TimeRange): string {
  if (range.kind === 'relative') return range.label
  if (range.from && range.to) return `${range.from} to ${range.to}`
  if (range.from) return `Since ${range.from}`
  if (range.to) return `Until ${range.to}`
  return 'Custom range'
}

function isSameRange(a: TimeRange, b: TimeRange): boolean {
  if (a.kind !== b.kind) return false
  if (a.kind === 'relative' && b.kind === 'relative') return a.days === b.days
  if (a.kind === 'absolute' && b.kind === 'absolute') return a.from === b.from && a.to === b.to
  return false
}

interface TimeRangePickerProps {
  value: TimeRange
  onChange: (range: TimeRange) => void
  label?: string
}

export function TimeRangePicker({ value, onChange, label = 'Time range' }: TimeRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [draftFrom, setDraftFrom] = useState(value.kind === 'absolute' ? value.from : '')
  const [draftTo, setDraftTo] = useState(value.kind === 'absolute' ? value.to : '')
  const containerRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const labelId = useId()
  const triggerId = useId()

  // Drafts are seeded when the popover OPENS rather than synced from an effect.
  // They were previously seeded only on mount, so applying an absolute range,
  // clearing filters back to the default, then reopening still showed the old
  // dates - and Apply silently restored the range that had been cleared.
  // Doing it here rather than in an effect also avoids the cascading render
  // that `react-hooks/set-state-in-effect` (correctly) rejects.
  // Nudge the panel back inside the viewport after it opens.
  //
  // CSS alone can't do this: the panel is anchored to the trigger, and on a
  // narrow screen the trigger's own horizontal offset is exactly what pushes a
  // near-viewport-wide panel off one edge or the other. Left-aligning fixed the
  // left clipping and promptly overflowed the right instead. Measuring is the
  // only way to know which way to shift.
  //
  // Written directly to the node rather than through state: this is positioning
  // against an external system (the viewport), and routing it through setState
  // in a layout effect is both a cascading render and what
  // `react-hooks/set-state-in-effect` rejects.
  useLayoutEffect(() => {
    if (!open) return

    const clamp = () => {
      const panel = panelRef.current
      if (!panel) return
      // Reset first: a stale offset from a previous measurement would otherwise
      // be measured as if it were the natural position. This is exactly what
      // went wrong when the viewport changed while the panel stayed open - the
      // old shift was still applied and the panel hung off the other edge.
      panel.style.transform = ''
      const rect = panel.getBoundingClientRect()
      const margin = 8
      let shift = 0
      if (rect.right > window.innerWidth - margin) shift = window.innerWidth - margin - rect.right
      if (rect.left + shift < margin) shift = margin - rect.left
      if (shift) panel.style.transform = `translateX(${Math.round(shift)}px)`
    }

    clamp()
    window.addEventListener('resize', clamp)
    return () => window.removeEventListener('resize', clamp)
  }, [open])

  const openPicker = () => {
    setDraftFrom(value.kind === 'absolute' ? value.from : '')
    setDraftTo(value.kind === 'absolute' ? value.to : '')
    setOpen(true)
  }

  // Close on outside click and on Escape, the way a menu is expected to behave.
  useEffect(() => {
    if (!open) return
    function onPointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    // Keyboard users tab out of the panel rather than clicking away, and a
    // panel left open then covers whatever they just focused while the trigger
    // still reports aria-expanded=true. Closing on focus leaving is the
    // keyboard equivalent of the outside click.
    function onFocusOut(event: FocusEvent) {
      const next = event.relatedTarget as Node | null
      if (next && containerRef.current?.contains(next)) return
      // A null relatedTarget is ambiguous: it covers tabbing out of the page
      // into browser chrome AND clicking a non-focusable part of the panel -
      // its heading, its padding - which blurs the input without the pointer
      // going anywhere. Closing on both discards the draft mid-edit; closing on
      // neither leaves the panel open over a page the user has tabbed away
      // from. `document.hasFocus()` separates them: it is false only once focus
      // has actually left the document.
      if (!next && document.hasFocus()) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    containerRef.current?.addEventListener('focusout', onFocusOut)
    const container = containerRef.current
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      container?.removeEventListener('focusout', onFocusOut)
    }
  }, [open])

  // Every path that closes the panel goes through here. Closing unmounts
  // whatever inside it had focus - a quick-range button, Apply, a date input -
  // so without restoring focus it lands on <body> and the next Tab restarts
  // elsewhere on the page. Escape had this; the commit paths did not.
  const closeAndRestoreFocus = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  const applyAbsolute = () => {
    if (!draftFrom && !draftTo) return
    onChange({ kind: 'absolute', from: draftFrom, to: draftTo })
    closeAndRestoreFocus()
  }

  return (
    <div className="flex flex-col gap-1" ref={containerRef}>
      {/* text-xs to match the sibling filter labels on this row. The id ties it
          to the trigger: without it a screen reader announces only the current
          value ("Last 30 days") with no indication of what it controls. */}
      <span id={labelId} className="text-xs font-medium text-textMuted">
        {label}
      </span>
      <div className="relative">
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          className="w-full justify-between gap-2 font-normal"
          onClick={() => (open ? setOpen(false) : openPicker())}
          id={triggerId}
          aria-haspopup="dialog"
          aria-expanded={open}
          // Both ids: the label names the control, the trigger's own text
          // supplies the current value.
          aria-labelledby={`${labelId} ${triggerId}`}
        >
          <span className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 shrink-0" />
            {describeTimeRange(value)}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
        </Button>

        {open && (
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Select time range"
            // Left-aligned on narrow screens, right-aligned from `sm` up. The
            // filter row wraps on mobile and can put this trigger at the left
            // edge; anchoring a near-viewport-wide panel to `right-0` off a
            // narrow trigger pushed its left edge negative and clipped the
            // quick-range column off-screen.
            className="absolute left-0 z-50 mt-2 flex w-[min(34rem,calc(100vw-2rem))] flex-col gap-4 rounded-md border border-border bg-surface p-4 shadow-lg sm:left-auto sm:right-0 sm:flex-row"
          >
            <div className="flex flex-col gap-1 sm:w-48">
              <p className="px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-textMuted">
                Quick ranges
              </p>
              {QUICK_RANGES.map(range => {
                const selected = isSameRange(value, range)
                return (
                  <button
                    key={range.label}
                    type="button"
                    onClick={() => {
                      onChange(range)
                      closeAndRestoreFocus()
                    }}
                    aria-current={selected}
                    className={`flex items-center justify-between rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-surfaceMuted ${
                      selected ? 'bg-surfaceMuted font-medium text-text' : 'text-textMuted'
                    }`}
                  >
                    {range.label}
                    {selected && <Check className="h-4 w-4" />}
                  </button>
                )
              })}
            </div>

            <div className="flex flex-col gap-3 sm:w-56 sm:border-l sm:border-border sm:pl-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-textMuted">
                Absolute range
              </p>
              <div className="flex flex-col gap-1">
                <label htmlFor="time-range-from" className="text-xs text-textMuted">
                  From
                </label>
                <Input
                  id="time-range-from"
                  type="date"
                  value={draftFrom}
                  onChange={e => setDraftFrom(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="time-range-to" className="text-xs text-textMuted">
                  To
                </label>
                <Input
                  id="time-range-to"
                  type="date"
                  value={draftTo}
                  onChange={e => setDraftTo(e.target.value)}
                />
              </div>
              <Button type="button" onClick={applyAbsolute} disabled={!draftFrom && !draftTo}>
                Apply range
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/** Whether a range is the default, used to decide if filters are "active". */
export function isDefaultTimeRange(range: TimeRange): boolean {
  return range.kind === 'relative' && range.days === DEFAULT_TIME_RANGE.days
}
